/**
 * Centralized In-Memory Data Store
 *
 * Replaces localStorage as the read/write layer for all project KPI data.
 * The store is hydrated from the PostgreSQL API on startup and kept in sync
 * through write-through on every mutation.
 *
 * Flow:
 *   1. App.tsx fetches data from API hooks (useApiScheduleData, useApiBudgetData, etc.)
 *   2. App.tsx calls hydrate*() to populate this store from API data
 *   3. Override files read from this store (NOT localStorage)
 *   4. Override files write to this store + API (NOT localStorage)
 *   5. CustomEvents still fire so React knows to re-render
 *
 * The store is a set of module-level Maps — no React context needed.
 * This is intentional: the scoring engine and adapter layers are pure
 * TypeScript and should not depend on React.
 */

import type { ScheduleProgressPoint, ScheduleCurveMeta } from "./ScheduleProgress/scheduleProgressOverrides";
import type { FinanceCostPoint, BudgetCurveMeta } from "./Budget/budgetOverrides";
import type { KpiProgressEntry } from "./KpiProgress/progressEntries";
import type { WorkflowRequest } from "../workflow/workflowTypes";
import type { PreflightItem } from "./PreFlight/preflightTypes";
import type { ProjectMilestone, ProjectPhase } from "./Milestones/milestoneTypes";

// ── Key helpers ──────────────────────────────────────────────

export function storeKey(projectGuid: string, instanceGuid?: string | null): string {
  const p = String(projectGuid || "").trim().toUpperCase();
  const i = String(instanceGuid || "").trim().toUpperCase();
  return i ? `${p}::${i}` : p;
}

// ── Schedule Progress ────────────────────────────────────────

export const scheduleSeriesStore = new Map<string, ScheduleProgressPoint[]>();
export const scheduleMetaStore = new Map<string, ScheduleCurveMeta>();

/**
 * Tracks the timestamp (Date.now()) of the most recent LOCAL write for each
 * schedule store key.  hydrateScheduleSeries uses this to skip overwriting
 * keys that were written locally within the last HYDRATION_GRACE_MS, preventing
 * the SignalR-triggered refetch from clobbering data that hasn't fully
 * round-tripped through the API yet.
 */
export const scheduleLocalWriteTs = new Map<string, number>();
const HYDRATION_GRACE_MS = 5_000; // 5 seconds

// ── Finance / Budget ─────────────────────────────────────────

export const budgetSeriesStore = new Map<string, FinanceCostPoint[]>();
export const budgetMetaStore = new Map<string, BudgetCurveMeta>();

/** Same pattern for finance writes. */
export const financeLocalWriteTs = new Map<string, number>();

// ── KPI Configuration ────────────────────────────────────────

/** Per-project KPI config (category_instances, thresholds, etc.) */
export const kpiConfigStore = new Map<string, any>();

// ── KPI Progress Entries ─────────────────────────────────────

/** All KPI progress entries (user-submitted actual progress updates). */
export const kpiProgressStore: KpiProgressEntry[] = [];

// ── Workflow Requests ────────────────────────────────────────

/** All workflow request queue entries. */
export const workflowStore: WorkflowRequest[] = [];

// ── Project Config Overrides ─────────────────────────────────

/** Project configuration overrides by project_guid */
export const projectConfigStore = new Map<string, any>();

// ── Project ACL Overrides ────────────────────────────────────

/** Project ACL entries */
export const projectAclStore: any[] = [];

/**
 * Monotonic version counter for projectAclStore.
 * Incremented whenever the store is modified.
 * React components can use this as a dependency to re-evaluate security.
 */
let _projectAclVersion = 0;
export function getProjectAclVersion(): number { return _projectAclVersion; }
export function bumpProjectAclVersion(): void { _projectAclVersion++; }

// ── Community Role Overrides ─────────────────────────────────

/** Community role assignments */
export const communityRoleStore: any[] = [];

// ── Community Membership Overrides ───────────────────────────

/** Community membership overrides */
export const communityMembershipStore: any[] = [];

// ── Community Config Overrides ───────────────────────────────

/** Community configuration overrides by community guid */
export const communityConfigStore = new Map<string, any>();

// ── Collection Project Links ─────────────────────────────────

/** Collection project links */
export const collectionProjectLinksStore: any[] = [];

// ── Project Workflow Overrides ───────────────────────────────

/** Project workflow overrides by project_guid */
export const projectWorkflowStore = new Map<string, any>();

// ── Project Stumps (bulk import) ─────────────────────────────

/** Imported project stumps */
export const projectStumpsStore: any[] = [];

// ── Standalone Milestones & Phases ──────────────────────────

/**
 * Manual milestones keyed by project GUID (uppercased).
 * Standard tier: user-created milestones. Professional tier: merged with Gantt milestones.
 */
export const milestoneStore = new Map<string, ProjectMilestone[]>();

/**
 * Manual phases keyed by project GUID (uppercased).
 * Standard tier: user-created phases. Professional tier: merged with Gantt phases.
 */
export const phaseStore = new Map<string, ProjectPhase[]>();

// ── Pre-Flight Items ────────────────────────────────────────

/**
 * Pre-flight items keyed by project GUID (uppercased).
 * Each project maps to its array of PreflightItem records.
 */
export const preflightStore = new Map<string, PreflightItem[]>();

/** Tracks local writes for pre-flight items (grace period). */
export const preflightLocalWriteTs = new Map<string, number>();

// ── Local write staleness check ──────────────────────────────
// Used by App.tsx scoring path to decide: read from in-memory store (local edit)
// or from API cache data (remote SignalR update). See Option A in the design doc.

const LOCAL_WRITE_STALENESS_MS = 10_000; // 10 seconds

/**
 * Returns true if the current tab wrote to the schedule store for this key
 * within the staleness window. If true, the in-memory store is authoritative
 * (the tab that made the edit). If false, API data should be preferred
 * (this tab did NOT make the edit — it's a remote update via SignalR).
 */
export function isScheduleLocalWriteRecent(projectGuid: string, instanceGuid?: string | null): boolean {
  const k = storeKey(projectGuid, instanceGuid ?? undefined);
  const ts = scheduleLocalWriteTs.get(k) ?? 0;
  return (Date.now() - ts) <= LOCAL_WRITE_STALENESS_MS;
}

/**
 * Returns true if ANY schedule key for the given project has a recent local write.
 * Used when we need a project-level check (e.g., when iterating instances).
 */
export function hasAnyScheduleLocalWriteRecent(projectGuid: string): boolean {
  const prefix = String(projectGuid || "").trim().toUpperCase();
  if (!prefix) return false;
  const now = Date.now();
  let found = false;
  scheduleLocalWriteTs.forEach((ts, k) => {
    if (!found && k.startsWith(prefix) && (now - ts) <= LOCAL_WRITE_STALENESS_MS) found = true;
  });
  return found;
}

/**
 * Returns true if the current tab wrote to the finance store for this key
 * within the staleness window.
 */
export function isFinanceLocalWriteRecent(projectGuid: string, instanceGuid?: string | null): boolean {
  const k = storeKey(projectGuid, instanceGuid ?? undefined);
  const ts = financeLocalWriteTs.get(k) ?? 0;
  return (Date.now() - ts) <= LOCAL_WRITE_STALENESS_MS;
}

/**
 * Returns true if ANY finance key for the given project has a recent local write.
 */
export function hasAnyFinanceLocalWriteRecent(projectGuid: string): boolean {
  const prefix = String(projectGuid || "").trim().toUpperCase();
  if (!prefix) return false;
  const now = Date.now();
  let found = false;
  financeLocalWriteTs.forEach((ts, k) => {
    if (!found && k.startsWith(prefix) && (now - ts) <= LOCAL_WRITE_STALENESS_MS) found = true;
  });
  return found;
}

// ── Hydration status ─────────────────────────────────────────

let _scheduleHydrated = false;
let _financeHydrated = false;
let _kpiConfigHydrated = false;
let _progressHydrated = false;
let _workflowHydrated = false;
let _projectConfigHydrated = false;
let _projectAclHydrated = false;
let _communityRoleHydrated = false;
let _communityMembershipHydrated = false;
let _communityConfigHydrated = false;
let _collectionProjectLinksHydrated = false;
let _projectWorkflowHydrated = false;
let _projectStumpsHydrated = false;
let _preflightHydrated = false;

export function isScheduleHydrated(): boolean { return _scheduleHydrated; }
export function isFinanceHydrated(): boolean { return _financeHydrated; }
export function isKpiConfigHydrated(): boolean { return _kpiConfigHydrated; }
export function isProgressHydrated(): boolean { return _progressHydrated; }
export function isWorkflowHydrated(): boolean { return _workflowHydrated; }
export function isProjectConfigHydrated(): boolean { return _projectConfigHydrated; }
export function isProjectAclHydrated(): boolean { return _projectAclHydrated; }
export function isCommunityRoleHydrated(): boolean { return _communityRoleHydrated; }
export function isCommunityMembershipHydrated(): boolean { return _communityMembershipHydrated; }
export function isCommunityConfigHydrated(): boolean { return _communityConfigHydrated; }
export function isCollectionProjectLinksHydrated(): boolean { return _collectionProjectLinksHydrated; }
export function isProjectWorkflowHydrated(): boolean { return _projectWorkflowHydrated; }
export function isProjectStumpsHydrated(): boolean { return _projectStumpsHydrated; }
export function isPreflightHydrated(): boolean { return _preflightHydrated; }

// ── DEV-1443: identity-switch reset ──────────────────────────
/**
 * Wipe every in-memory store and mark all domains un-hydrated. Called on ANY
 * auth identity change (login/register/logout/2FA/impersonate) so a prior
 * user's cached data can never render for the next identity on a shared
 * browser. Consumers read `isXHydrated()` — flipping the flags false makes
 * them show loading (never stale) until the new identity's data re-hydrates.
 * Device-level state (theme/language/calendar in localStorage) is NOT touched.
 */
export function resetAllStores(): void {
  // Maps
  scheduleSeriesStore.clear();
  scheduleMetaStore.clear();
  scheduleLocalWriteTs.clear();
  budgetSeriesStore.clear();
  budgetMetaStore.clear();
  financeLocalWriteTs.clear();
  kpiConfigStore.clear();
  projectConfigStore.clear();
  communityConfigStore.clear();
  projectWorkflowStore.clear();
  milestoneStore.clear();
  phaseStore.clear();
  preflightStore.clear();
  preflightLocalWriteTs.clear();
  // Arrays
  kpiProgressStore.length = 0;
  workflowStore.length = 0;
  projectAclStore.length = 0;
  communityRoleStore.length = 0;
  communityMembershipStore.length = 0;
  collectionProjectLinksStore.length = 0;
  projectStumpsStore.length = 0;
  // Hydration flags → false (re-hydrate for the new identity on next fetch)
  _scheduleHydrated = false;
  _financeHydrated = false;
  _kpiConfigHydrated = false;
  _progressHydrated = false;
  _workflowHydrated = false;
  _projectConfigHydrated = false;
  _projectAclHydrated = false;
  _communityRoleHydrated = false;
  _communityMembershipHydrated = false;
  _communityConfigHydrated = false;
  _collectionProjectLinksHydrated = false;
  _projectWorkflowHydrated = false;
  _projectStumpsHydrated = false;
  _preflightHydrated = false;
}

// ── Hydration from API data ──────────────────────────────────

/**
 * Populate schedule series store from API data (seed-shape records).
 * Called by App.tsx after useApiScheduleData resolves.
 */
export function hydrateScheduleSeries(
  apiData: Record<string, any>[],
): void {
  const now = Date.now();

  // Group by (project_GUID, instance_guid) → key
  const byKey = new Map<string, ScheduleProgressPoint[]>();
  for (const r of apiData) {
    const pg = String(r.project_GUID || r.projectId || "").trim();
    const ig = String(r.instance_guid || r.instanceGuid || "").trim();
    if (!pg) continue;
    const k = storeKey(pg, ig || undefined);
    if (!byKey.has(k)) byKey.set(k, []);
    byKey.get(k)!.push({
      project_guid: pg,
      instance_guid: ig || undefined,
      reporting_date: String(r.reporting_date || r.reportingDate || ""),
      baseline_complete: r.baseline_complete ?? r.baselineComplete ?? null,
      actual_complete: r.actual_complete ?? r.actualComplete ?? null,
      forecast_complete: r.forecast_complete ?? r.forecastComplete ?? null,
      // DEV-851: Carry submission timestamp through hydration so
      // DataReliabilityTab can compute per-row submission lateness.
      created_at: r.created_at ?? r.createdAt ?? undefined,
    });
  }

  // Collect all keys that are present in the incoming API data.
  const incomingKeys = new Set(byKey.keys());

  // Remove stale keys that are no longer in the API data,
  // but ONLY if they weren't written locally within the grace window.
  for (const existingKey of Array.from(scheduleSeriesStore.keys())) {
    if (!incomingKeys.has(existingKey)) {
      const localTs = scheduleLocalWriteTs.get(existingKey) ?? 0;
      if (now - localTs > HYDRATION_GRACE_MS) {
        scheduleSeriesStore.delete(existingKey);
      }
    }
  }

  // Populate from API data — but skip keys that were written locally
  // within the grace window (those local writes are fresher than the API).
  byKey.forEach((pts, k) => {
    const localTs = scheduleLocalWriteTs.get(k) ?? 0;
    if (now - localTs <= HYDRATION_GRACE_MS) {
      // Local write is recent — keep local data, skip API overwrite.
      return;
    }
    pts.sort((a, b) => a.reporting_date.localeCompare(b.reporting_date));
    scheduleSeriesStore.set(k, pts);
  });

  _scheduleHydrated = true;

  // Notify chart components that schedule data is now available.
  try { window.dispatchEvent(new CustomEvent("l4e_schedule_progress_changed")); } catch { /* ignore */ }
}

/**
 * Populate finance series store from API data (seed-shape records).
 * Called by App.tsx after useApiBudgetData resolves.
 */
export function hydrateBudgetSeries(
  apiData: Record<string, any>[],
): void {
  const now = Date.now();

  const byKey = new Map<string, FinanceCostPoint[]>();
  for (const r of apiData) {
    const pg = String(r.project_GUID || r.projectId || "").trim();
    const ig = String(r.instance_guid || r.instanceGuid || "").trim();
    if (!pg) continue;
    const k = storeKey(pg, ig || undefined);
    if (!byKey.has(k)) byKey.set(k, []);
    byKey.get(k)!.push({
      project_guid: pg,
      instance_guid: ig || undefined,
      reporting_date: String(r.reporting_date || r.reportingDate || ""),
      baseline_budget: r.baseline_budget ?? r.baselineBudget ?? null,
      actual_spend: r.actual_spend ?? r.actualSpend ?? null,
      forecast_spend: r.forecast_spend ?? r.forecastSpend ?? null,
      // DEV-851: Carry submission timestamp through hydration. See Schedule branch.
      created_at: r.created_at ?? r.createdAt ?? undefined,
    });
  }

  const incomingKeys = new Set(byKey.keys());

  // Remove stale keys not in API data (unless recently written locally).
  for (const existingKey of Array.from(budgetSeriesStore.keys())) {
    if (!incomingKeys.has(existingKey)) {
      const localTs = financeLocalWriteTs.get(existingKey) ?? 0;
      if (now - localTs > HYDRATION_GRACE_MS) {
        budgetSeriesStore.delete(existingKey);
      }
    }
  }

  // Populate from API — skip keys with recent local writes.
  byKey.forEach((pts, k) => {
    const localTs = financeLocalWriteTs.get(k) ?? 0;
    if (now - localTs <= HYDRATION_GRACE_MS) return;
    pts.sort((a, b) => a.reporting_date.localeCompare(b.reporting_date));
    budgetSeriesStore.set(k, pts);
  });

  _financeHydrated = true;

  // Notify chart components that finance data is now available.
  try { window.dispatchEvent(new CustomEvent("l4e_finance_cost_changed")); } catch { /* ignore */ }
}

/**
 * Populate schedule & budget META stores from the CurveMeta API data.
 * Called by App.tsx after useApiCurveMeta resolves.
 *
 * This is the missing hydration step: the API returns curve metadata
 * (reporting_schedule, baseline dates, etc.) but previously it only
 * lived in the useApiCurveMeta hook — never pushed into the in-memory
 * stores that DataReliabilityTab and other consumers read from.
 */
export function hydrateCurveMeta(
  scheduleMeta: Record<string, Record<string, any>>,
  budgetMeta: Record<string, Record<string, any>>,
): void {
  // Hydrate schedule meta — skip keys that already have data
  // (set by a recent local write via the thermometer UI).
  for (const [key, meta] of Object.entries(scheduleMeta)) {
    if (!scheduleMetaStore.has(key)) {
      scheduleMetaStore.set(key, meta as ScheduleCurveMeta);
    }
  }

  // Hydrate budget meta — same skip-if-present logic.
  for (const [key, meta] of Object.entries(budgetMeta)) {
    if (!budgetMetaStore.has(key)) {
      budgetMetaStore.set(key, meta as BudgetCurveMeta);
    }
  }

  // Notify components so they pick up the newly-available meta.
  try { window.dispatchEvent(new CustomEvent("l4e_schedule_progress_changed")); } catch { /* ignore */ }
  try { window.dispatchEvent(new CustomEvent("l4e_finance_cost_changed")); } catch { /* ignore */ }
}

/**
 * Populate KPI config store from API data.
 * Called by App.tsx after useApiKpiConfigs resolves.
 *
 * @param configsByProject — Map<projectGuid, { schedule, finance, mission }>
 */
export function hydrateKpiConfigs(
  configsByProject: Map<string, { schedule: Record<string, any>[]; finance: Record<string, any>[]; mission?: Record<string, any>[] }>,
): void {
  kpiConfigStore.clear();

  configsByProject.forEach((cfg, pg) => {
    const k = pg.trim().toUpperCase();
    kpiConfigStore.set(k, {
      project_guid: pg,
      category_instances: {
        schedule: cfg.schedule || [],
        finance: cfg.finance || [],
        mission: cfg.mission || [],
      },
    });
  });

  _kpiConfigHydrated = true;

  // Notify components that depend on kpi_config data.
  try { window.dispatchEvent(new CustomEvent("l4e_schedule_progress_changed")); } catch { /* ignore */ }
  try { window.dispatchEvent(new CustomEvent("l4e_finance_cost_changed")); } catch { /* ignore */ }
  try { window.dispatchEvent(new CustomEvent("l4e_mission_updated")); } catch { /* ignore */ }
}

/**
 * Populate KPI progress entries from API data.
 * Called by App.tsx after useApiKpiProgress resolves.
 */
export function hydrateProgressEntries(
  apiData: Record<string, any>[],
): void {
  kpiProgressStore.length = 0;
  for (const r of apiData) {
    kpiProgressStore.push({
      entry_guid: String(r.entry_guid || r.entryGuid || ""),
      project_guid: String(r.project_guid || r.projectId || ""),
      kpi_key: r.kpi_key || r.kpiKey || "schedule",
      reporting_date: String(r.reporting_date || r.reportingDate || ""),
      actual_complete: Number(r.actual_complete ?? r.actualComplete ?? 0),
      note: r.note || undefined,
      evidence_url: r.evidence_url || r.evidenceUrl || undefined,
      status: r.status || "draft",
      workflow_guid: r.workflow_guid || r.workflowGuid || undefined,
      created_by: String(r.created_by || r.createdBy || ""),
      created_at: String(r.created_at || r.createdAt || ""),
      updated_at: String(r.updated_at || r.updatedAt || ""),
      approved_by: r.approved_by || r.approvedBy || undefined,
      approved_at: r.approved_at || r.approvedAt || undefined,
    } as KpiProgressEntry);
  }
  _progressHydrated = true;
}

/**
 * Populate workflow requests from API data.
 * Called by App.tsx after useApiWorkflows resolves — both on initial mount
 * and after SignalR-driven cache invalidations (DEV-1023).
 *
 * DEV-1023 fix: must dispatch l4e_workflows_updated after rewriting the store
 * so subscribers (App.tsx approvals-badge counter, ContentApprovalsPage's
 * subscribeWorkflows callback) re-read against the fresh data. Without this
 * dispatch the SignalR push reached the wire but the badge never refreshed,
 * because the immediate dispatch from signalrConnection.ts fires BEFORE the
 * cache refetch completes — leaving subscribers reading stale state.
 */
export function hydrateWorkflows(
  apiData: Record<string, any>[],
): void {
  workflowStore.length = 0;
  for (const r of apiData) {
    workflowStore.push({
      workflow_guid: String(r.workflow_guid || r.workflowGuid || ""),
      title: r.title || undefined,
      description: r.description || undefined,
      target_type: r.target_type || r.targetType || "project",
      target_guid: String(r.target_guid || r.targetGuid || ""),
      action_type: r.action_type || r.actionType || "generic",
      payload: r.payload || {},
      status: r.status || "draft",
      version: r.version || 1,
      created_by: String(r.created_by || r.createdBy || ""),
      submitted_by: r.submitted_by || r.submittedBy || undefined,
      assigned_to_guids: Array.isArray(r.assigned_to_guids || r.assignedToGuids)
        ? (r.assigned_to_guids || r.assignedToGuids)
        : [],
      history: Array.isArray(r.history) ? r.history : [],
      created_at: String(r.created_at || r.createdAt || ""),
      updated_at: String(r.updated_at || r.updatedAt || ""),
    } as WorkflowRequest);
  }
  _workflowHydrated = true;
  try {
    window.dispatchEvent(new CustomEvent("l4e_workflows_updated"));
  } catch { /* SSR guard */ }
}

/**
 * Populate project config overrides store from API data.
 * Merges API data with existing local overrides so that locally-set values
 * (e.g. a hero photo just uploaded) are not overwritten by a stale API read
 * that may arrive before the PUT has fully committed.
 */
export function hydrateProjectConfigs(
  apiData: Record<string, any>[],
): void {
  // Build a set of GUIDs present in the new API data
  const incomingGuids = new Set<string>();
  for (const cfg of (apiData || [])) {
    const pg = String(cfg.project_guid || cfg.project_GUID || "").trim().toUpperCase();
    if (!pg) continue;
    incomingGuids.add(pg);

    const existing = projectConfigStore.get(pg);
    if (existing) {
      // Merge: API data is the base, but preserve any locally-set fields
      // that are newer (e.g. tile_image_url set seconds ago via setProjectOverride).
      // Local overrides win for fields that are truthy locally but differ from API.
      const merged = { ...cfg };
      for (const key of Object.keys(existing)) {
        const localVal = (existing as any)[key];
        const apiVal = (cfg as any)[key];
        // Keep local value if it's truthy and different from API
        // (the API may not have committed the update yet)
        if (localVal && localVal !== apiVal) {
          (merged as any)[key] = localVal;
        }
      }
      projectConfigStore.set(pg, merged);
    } else {
      projectConfigStore.set(pg, cfg);
    }
  }

  // Remove entries that are no longer in the API data
  // (project was deleted server-side)
  for (const key of Array.from(projectConfigStore.keys())) {
    if (!incomingGuids.has(key)) {
      projectConfigStore.delete(key);
    }
  }

  _projectConfigHydrated = true;
}

/**
 * Populate project ACL store from API data.
 * Called after useApiProjectAcl resolves.
 */
export function hydrateProjectAcl(
  apiData: Record<string, any>[],
): void {
  projectAclStore.length = 0;

  // Deduplicate by (project_guid, user_guid) — keep only ONE entry per combination.
  // If the API returns duplicate rows (e.g. from a role change that created a new row
  // without deleting the old one), the LAST entry for each key wins. To ensure the
  // most-recently-created entry takes precedence, sort by id ascending before dedup
  // (higher id = created later = should win).
  const sorted = [...(apiData || [])].sort((a, b) => {
    const aId = String(a.id || "");
    const bId = String(b.id || "");
    return aId.localeCompare(bId);
  });
  const dedupMap = new Map<string, Record<string, any>>();
  for (const entry of sorted) {
    const pg = String(entry.project_guid || entry.projectGuid || "").toUpperCase();
    const ug = String(entry.user_guid || entry.userGuid || "").toUpperCase();
    const key = `${pg}::${ug}`;
    dedupMap.set(key, entry); // last (highest id) wins
  }

  for (const entry of dedupMap.values()) {
    projectAclStore.push({
      id: entry.id || undefined, // Preserve API record ID for DELETE calls
      project_guid: String(entry.project_guid || entry.projectGuid || ""),
      user_guid: String(entry.user_guid || entry.userGuid || ""),
      role: entry.role || "viewer",
      granted_by_user_guid: entry.granted_by_user_guid || entry.grantedByUserGuid || undefined,
      granted_at: entry.granted_at || entry.grantedAt || undefined,
      is_active: entry.is_active !== false,
    });
  }
  _projectAclHydrated = true;
  bumpProjectAclVersion();
}

/**
 * Populate community role store from API data.
 * Called when API endpoints are ready (not yet implemented).
 */
export function hydrateCommunityRoles(
  apiData: Record<string, any>[],
): void {
  communityRoleStore.length = 0;
  for (const entry of (apiData || [])) {
    communityRoleStore.push({
      user_guid: String(entry.user_guid || entry.userGuid || ""),
      community_entity_guid: String(entry.community_entity_guid || entry.communityEntityGuid || ""),
      role: entry.role || "reader",
      is_active: entry.is_active !== false,
    });
  }
  _communityRoleHydrated = true;
}

/**
 * Populate community membership store from API data.
 *
 * DEV-1372 slice 1: community_memberships is the single source of truth
 * for "user belongs to community." The store keeps membership_type so
 * declared-community reads (home / operating) resolve from the one
 * source instead of the retired security_users columns.
 */
export function hydrateCommunityMemberships(
  apiData: Record<string, any>[],
): void {
  communityMembershipStore.length = 0;
  for (const entry of (apiData || [])) {
    communityMembershipStore.push({
      id: entry.id || undefined,
      user_guid: String(entry.user_guid || entry.userGuid || ""),
      community_entity_guid: String(entry.community_entity_guid || entry.communityEntityGuid || ""),
      membership_type: String(entry.membership_type || entry.membershipType || "joined"),
      status: entry.status || "active",
      started_at: entry.started_at || entry.startedAt || undefined,
      ended_at: entry.ended_at || entry.endedAt || undefined,
    });
  }
  _communityMembershipHydrated = true;
  window.dispatchEvent(new CustomEvent('l4e_community_membership_changed'));
}

/**
 * DEV-1372 slice 1 — the single membership read accessors. Every
 * "is member of X / my member communities / declared home-or-operating
 * community" read goes through these (or through the reactive
 * useApiSecurityData().communityMemberships rows they mirror) — never
 * through security_users home/operational columns, V1 role derivation,
 * or ad-hoc unions.
 *
 * GUID comparisons are case-insensitive per CLAUDE.md.
 */

/** Upper-cased GUIDs of communities where the user has an active membership row. */
export function getMemberCommunityGuids(userGuid: string): Set<string> {
  const set = new Set<string>();
  const me = String(userGuid || "").toUpperCase();
  if (!me) return set;
  for (const m of communityMembershipStore) {
    if (
      String(m.user_guid || "").toUpperCase() === me &&
      String(m.status || "active").toLowerCase() === "active"
    ) {
      set.add(String(m.community_entity_guid || "").toUpperCase());
    }
  }
  return set;
}

/**
 * The user's declared community of the given type ('home' | 'operating'),
 * from the active membership row of that type. Returns the row's GUID in
 * its stored casing, or null when undeclared.
 */
export function getDeclaredCommunityGuid(
  userGuid: string,
  membershipType: "home" | "operating",
): string | null {
  const me = String(userGuid || "").toUpperCase();
  if (!me) return null;
  for (const m of communityMembershipStore) {
    if (
      String(m.user_guid || "").toUpperCase() === me &&
      String(m.membership_type || "").toLowerCase() === membershipType &&
      String(m.status || "active").toLowerCase() === "active"
    ) {
      const g = String(m.community_entity_guid || "").trim();
      if (g) return g;
    }
  }
  return null;
}

/**
 * Populate community config overrides store from API data.
 * Called when API endpoints are ready (not yet implemented).
 */
export function hydrateCommunityConfigs(
  apiData: Record<string, any>[],
): void {
  communityConfigStore.clear();
  for (const cfg of (apiData || [])) {
    const cg = String(cfg.community_guid || cfg.guid || cfg.entity_guid || "").trim().toUpperCase();
    if (cg) {
      communityConfigStore.set(cg, cfg);
    }
  }
  _communityConfigHydrated = true;
  try {
    window.dispatchEvent(new CustomEvent("l4e_community_config_changed"));
  } catch { /* SSR guard */ }
}

/**
 * Populate collection project links store from API data.
 * Called when API endpoints are ready (not yet implemented).
 */
export function hydrateCollectionProjectLinks(
  apiData: Record<string, any>[],
): void {
  collectionProjectLinksStore.length = 0;
  for (const link of (apiData || [])) {
    collectionProjectLinksStore.push({
      collection_guid: String(link.collection_guid || link.collectionGuid || ""),
      project_guid: String(link.project_guid || link.projectGuid || ""),
      is_active: link.is_active !== false,
      added_by_user_guid: link.added_by_user_guid || link.addedByUserGuid || undefined,
      added_at: link.added_at || link.addedAt || undefined,
    });
  }
  _collectionProjectLinksHydrated = true;
}

/**
 * Populate project workflow overrides store from API data.
 * Called when API endpoints are ready (not yet implemented).
 */
export function hydrateProjectWorkflows(
  apiData: Record<string, any>[],
): void {
  projectWorkflowStore.clear();
  for (const wf of (apiData || [])) {
    const pg = String(wf.project_guid || wf.projectGuid || "").trim().toUpperCase();
    if (pg) {
      projectWorkflowStore.set(pg, {
        project_guid: pg,
        disabled_workflow_action_types: Array.isArray(wf.disabled_workflow_action_types)
          ? wf.disabled_workflow_action_types
          : [],
      });
    }
  }
  _projectWorkflowHydrated = true;
}

/**
 * Populate project stumps store from API data or bulk import.
 * Called when stumps are loaded from file or API.
 */
export function hydrateProjectStumps(
  apiData: Record<string, any>[],
): void {
  projectStumpsStore.length = 0;
  for (const stump of (apiData || [])) {
    const p: any = { ...(stump || {}) };
    const g = String(p.project_guid || p.project_GUID || p.guid || "").trim();
    if (g) {
      p.project_guid = p.project_guid || g;
      p.project_GUID = p.project_GUID || g;
    }
    projectStumpsStore.push(p);
  }
  _projectStumpsHydrated = true;
}

/**
 * Populate pre-flight items store from API data.
 * Called by App.tsx after useApiPreflightData resolves.
 *
 * Groups items by project_guid (uppercased) and stores them sorted by sort_order.
 */
export function hydratePreflightItems(
  apiData: Record<string, any>[],
): void {
  const now = Date.now();
  const byProject = new Map<string, PreflightItem[]>();

  for (const r of (apiData || [])) {
    const pg = String(r.project_guid || r.projectGuid || "").trim().toUpperCase();
    if (!pg) continue;

    if (!byProject.has(pg)) byProject.set(pg, []);
    byProject.get(pg)!.push({
      item_guid: String(r.item_guid || r.itemGuid || ""),
      project_guid: String(r.project_guid || r.projectGuid || ""),
      phase: r.phase || r.Phase || "preparation",
      category: r.category || "materials",
      label: r.label || "",
      description: r.description || undefined,
      requirement_type: r.requirement_type || r.requirementType || "binary",
      target_quantity: r.target_quantity ?? r.targetQuantity ?? null,
      unit: r.unit || null,
      current_quantity: Number(r.current_quantity ?? r.currentQuantity ?? 0),
      is_fulfilled: Boolean(r.is_fulfilled ?? r.isFulfilled ?? false),
      visibility: r.visibility || "internal",
      mission_subkpi_key: r.mission_subkpi_key || r.missionSubkpiKey || null,
      tags: Array.isArray(r.tags) ? r.tags : [],
      sort_order: Number(r.sort_order ?? r.sortOrder ?? 0),
      created_by: String(r.created_by || r.createdBy || ""),
      created_at: String(r.created_at || r.createdAt || ""),
      updated_at: String(r.updated_at || r.updatedAt || ""),
      source_template_guid: r.source_template_guid || r.sourceTemplateGuid || null,
      // ── Fields previously missing from hydration (DEV-643) ────────
      state: (r.state || (r.is_fulfilled || r.isFulfilled ? "met" : "seeking")) as any,
      priority: (r.priority || "important") as any,
      descriptor_tags: Array.isArray(r.descriptor_tags || r.descriptorTags) ? (r.descriptor_tags || r.descriptorTags) : [],
      matching_conditions: r.matching_conditions || r.matchingConditions || null,
      linked_offer_guid: r.linked_offer_guid || r.linkedOfferGuid || null,
      withdrawn_at: r.withdrawn_at || r.withdrawnAt || null,
      depends_on: Array.isArray(r.depends_on || r.dependsOn) ? (r.depends_on || r.dependsOn) : [],
      cost_category: r.cost_category || r.costCategory || r.category || "materials",
      unit_cost: r.unit_cost ?? r.unitCost ?? null,
      actual_cost: r.actual_cost ?? r.actualCost ?? null,
      cost_currency: r.cost_currency || r.costCurrency || null,
      cost_note: r.cost_note || r.costNote || null,
      estimated_total: r.estimated_total ?? r.estimatedTotal ?? null,
      actual_total: r.actual_total ?? r.actualTotal ?? null,
      gantt_task_id: r.gantt_task_id || r.ganttTaskId || null,
      percent_complete: Number(r.percent_complete ?? r.percentComplete ?? 0),
      // ── Fulfillment / Acquisition fields (DEV-647) ──────────
      fulfillment_note: r.fulfillment_note || r.fulfillmentNote || null,
      assignee_guid: r.assignee_guid || r.assigneeGuid || null,
    });
  }

  const incomingKeys = new Set(byProject.keys());

  // Remove stale keys unless recently written locally.
  for (const existingKey of Array.from(preflightStore.keys())) {
    if (!incomingKeys.has(existingKey)) {
      const localTs = preflightLocalWriteTs.get(existingKey) ?? 0;
      if (now - localTs > HYDRATION_GRACE_MS) {
        preflightStore.delete(existingKey);
      }
    }
  }

  // Populate from API — skip keys with recent local writes.
  byProject.forEach((items, k) => {
    const localTs = preflightLocalWriteTs.get(k) ?? 0;
    if (now - localTs <= HYDRATION_GRACE_MS) return;
    items.sort((a, b) => a.sort_order - b.sort_order);
    preflightStore.set(k, items);
  });

  _preflightHydrated = true;

  try { window.dispatchEvent(new CustomEvent("l4e_preflight_changed")); } catch { /* ignore */ }
}

// ── localStorage is DEAD ─────────────────────────────────────
// All data comes from PostgreSQL via API hydration. No localStorage reads.
// This no-op stub exists only so existing callers don't break at import time.
// TODO: Remove all ensureWarm()/warmStartFromLocalStorage() call sites.
export function warmStartFromLocalStorage(): void {
  // Intentionally empty — localStorage is no longer a data source.
}
